
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WheelPicker, WheelPickerWrapper } from '@ncdai/react-wheel-picker';
import { TimelineSection } from '@/types';
import { findSectionForYear } from '@/utils/timelineSectionHelpers';

export interface DateSelection {
  year?: string;
  month?: string;
  day?: string;
  label: string;
  isSkipped?: boolean;
}

interface WheelPickerOption {
  label: string;
  value: string;
}

interface UplivionDateWheelPickerProps {
  onDateSelect: (dateData: DateSelection) => void;
  onYearChange?: (year: string) => void;
  onSectionChange?: (newSection: TimelineSection) => void;
  onLifePhaseUpdate?: (section: TimelineSection | null) => void;
  showToday?: boolean;
  initialValues?: Partial<DateSelection>;
  timelineSections?: TimelineSection[];
  currentSectionId?: string;
  userBirthdate?: string;
}

const UplivionDateWheelPicker = ({ 
  onDateSelect, 
  onYearChange,
  onSectionChange,
  onLifePhaseUpdate,
  showToday = false, 
  initialValues = {},
  timelineSections = [],
  currentSectionId,
  userBirthdate
}: UplivionDateWheelPickerProps) => {
  const { t } = useTranslation('memory');
  
  const [selectedYear, setSelectedYear] = useState(initialValues.year || '');
  const [selectedMonth, setSelectedMonth] = useState(initialValues.month || '');
  const [selectedDay, setSelectedDay] = useState(initialValues.day || '');

  // Debug logging
  console.log('🎯 UplivionDateWheelPicker render:', {
    initialValues,
    selectedYear,
    selectedMonth,
    selectedDay,
    currentSectionId,
    timelineSectionsLength: timelineSections.length,
    userBirthdate,
    firstSection: timelineSections[0]
  });

  // Update state when initialValues change
  useEffect(() => {
    console.log('🎯 UplivionDateWheelPicker useEffect triggered:', {
      initialValues,
      currentState: { selectedYear, selectedMonth, selectedDay }
    });

    if (initialValues.year !== undefined && initialValues.year !== selectedYear) {
      console.log('🎯 Updating selectedYear from', selectedYear, 'to', initialValues.year);
      setSelectedYear(initialValues.year);
    }
    if (initialValues.month !== undefined && initialValues.month !== selectedMonth) {
      console.log('🎯 Updating selectedMonth from', selectedMonth, 'to', initialValues.month);
      setSelectedMonth(initialValues.month);
    }
    if (initialValues.day !== undefined && initialValues.day !== selectedDay) {
      console.log('🎯 Updating selectedDay from', selectedDay, 'to', initialValues.day);
      setSelectedDay(initialValues.day);
    }
  }, [initialValues.year, initialValues.month, initialValues.day]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  
  const months = [
    { value: '1', label: t('months.january') },
    { value: '2', label: t('months.february') },
    { value: '3', label: t('months.march') },
    { value: '4', label: t('months.april') },
    { value: '5', label: t('months.may') },
    { value: '6', label: t('months.june') },
    { value: '7', label: t('months.july') },
    { value: '8', label: t('months.august') },
    { value: '9', label: t('months.september') },
    { value: '10', label: t('months.october') },
    { value: '11', label: t('months.november') },
    { value: '12', label: t('months.december') },
  ];

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  const days = selectedYear && selectedMonth 
    ? Array.from({ length: getDaysInMonth(parseInt(selectedYear), parseInt(selectedMonth)) }, (_, i) => i + 1)
    : Array.from({ length: 31 }, (_, i) => i + 1);

  const handleYearChange = (year: string) => {
    console.log('🎯 handleYearChange called with:', year);
    setSelectedYear(year);
    
    // Check if year should trigger section change
    if (year && year !== '' && onSectionChange && timelineSections.length > 0 && userBirthdate) {
      console.log('🎯 Checking for section change with data:', {
        year,
        currentSectionId,
        timelineSectionsCount: timelineSections.length,
        userBirthdate,
        hasOnSectionChange: !!onSectionChange
      });

      const newSection = findSectionForYear(year, timelineSections, userBirthdate);
      console.log('🎯 findSectionForYear returned:', newSection);
      
      if (newSection && newSection.id !== currentSectionId) {
        console.log('🎯 Year changed requires section change:', {
          year,
          currentSectionId,
          newSectionId: newSection.id,
          newSectionName: newSection.name
        });
        onSectionChange(newSection);
      } else {
        console.log('🎯 No section change needed:', {
          newSectionFound: !!newSection,
          newSectionId: newSection?.id,
          currentSectionId,
          sameSection: newSection?.id === currentSectionId
        });
      }
    } else {
      console.log('🎯 Skipping section change check:', {
        hasYear: !!year,
        hasOnSectionChange: !!onSectionChange,
        hasSections: timelineSections.length > 0,
        hasUserBirthdate: !!userBirthdate
      });
    }

    if (onYearChange && year && year !== '') {
      console.log('🎯 Calling onYearChange callback with:', year);
      onYearChange(year);
    }
  };

  const allNotSure = selectedYear === '' && selectedMonth === '' && selectedDay === '';
  const hasYear = selectedYear !== '';
  const canContinue = hasYear;
  const canSkip = allNotSure;
  const bothDisabled = !canContinue && !canSkip;

  const getYearOptions = (): WheelPickerOption[] => {
    const yearOptions: WheelPickerOption[] = [
      { value: '', label: t('chat.notSure') }
    ];
    
    years.forEach(year => {
      yearOptions.push({ value: year.toString(), label: year.toString() });
    });
    
    return yearOptions;
  };

  const getMonthOptions = (): WheelPickerOption[] => {
    const monthOptions: WheelPickerOption[] = [
      { value: '', label: t('chat.notSure') }
    ];
    
    months.forEach(month => {
      monthOptions.push({ value: month.value, label: month.label });
    });
    
    return monthOptions;
  };

  const getDayOptions = (): WheelPickerOption[] => {
    const dayOptions: WheelPickerOption[] = [
      { value: '', label: t('chat.notSure') }
    ];
    
    days.forEach(day => {
      dayOptions.push({ value: day.toString(), label: day.toString() });
    });
    
    return dayOptions;
  };

  const handleTodaySelect = () => {
    const today = new Date();
    const dateData: DateSelection = {
      year: today.getFullYear().toString(),
      month: (today.getMonth() + 1).toString(),
      day: today.getDate().toString(),
      label: t('chat.today')
    };
    onDateSelect(dateData);
  };

  const handleDateSelect = () => {
    let label = '';
    const dateData: DateSelection = { label: '' };

    if (selectedYear && selectedMonth && selectedDay) {
      dateData.year = selectedYear;
      dateData.month = selectedMonth;
      dateData.day = selectedDay;
      label = `${selectedDay}.${selectedMonth}.${selectedYear}`;
    } else {
      // Partial date selection
      const parts = [];
      if (selectedYear && selectedYear !== '') {
        dateData.year = selectedYear;
        parts.push(selectedYear);
      }
      if (selectedMonth && selectedMonth !== '') {
        dateData.month = selectedMonth;
        parts.push(months.find(m => m.value === selectedMonth)?.label);
      }
      if (selectedDay && selectedDay !== '') {
        dateData.day = selectedDay;
        parts.push(selectedDay);
      }
      label = parts.length > 0 ? parts.join(', ') : t('chat.approximateDate');
    }

    dateData.label = label;
    onDateSelect(dateData);
  };

  const handleSkip = () => {
    const dateData: DateSelection = {
      label: t('chat.skipDate'),
      isSkipped: true
    };
    onDateSelect(dateData);
  };

  return (
    <div className="space-y-4">
      {showToday && (
        <button
          onClick={handleTodaySelect}
          className="w-full p-3 bg-uplivion-blue text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
        >
          {t('chat.today')}
        </button>
      )}

      <div className="w-full rounded-lg border border-zinc-200 bg-white px-1 shadow-xs dark:border-zinc-700/80 dark:bg-zinc-900 *:data-rwp:first:*:data-rwp-highlight-wrapper:rounded-s-md *:data-rwp:last:*:data-rwp-highlight-wrapper:rounded-e-md" data-rwp-wrapper="true">
        <WheelPickerWrapper>
          {/* Year Column */}
          <WheelPicker
            options={getYearOptions()}
            value={selectedYear}
            onValueChange={handleYearChange}
            infinite={false}
          />

          {/* Month Column */}
          <WheelPicker
            options={getMonthOptions()}
            value={selectedMonth}
            onValueChange={(value) => setSelectedMonth(value)}
            infinite={false}
          />

          {/* Day Column */}
          <WheelPicker
            options={getDayOptions()}
            value={selectedDay}
            onValueChange={(value) => setSelectedDay(value)}
            infinite={false}
          />
        </WheelPickerWrapper>
      </div>

      <div className="flex gap-2">
        {canSkip && (
          <button
            onClick={handleSkip}
            className="flex-1 p-3 bg-gray-500 text-white rounded-xl font-medium hover:bg-gray-600 transition-colors"
          >
            {t('chat.skip')}
          </button>
        )}
        
        {canContinue && (
          <button
            onClick={handleDateSelect}
            className="flex-1 p-3 bg-uplivion-blue text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            {t('buttons.next')}
          </button>
        )}

        {bothDisabled && (
          <button
            disabled
            className="flex-1 p-3 bg-gray-300 text-gray-500 rounded-xl font-medium cursor-not-allowed"
          >
            {t('buttons.next')}
          </button>
        )}
      </div>
    </div>
  );
};

export default UplivionDateWheelPicker;
